import React from 'react';

import {
	ADMIN_HOME_ID,
	CONTAINER_ADMIN_HOME_ID,
	CONTAINER_HOME_ID,
} from '@confluence/app-navigation/entry-points/navigationIDs.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { LoadablePaint } from '@confluence/loadable';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { getCompanyHubSideNav } from '@confluence/breadcrumbs/entry-points/getCompanyHubSideNav.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { Redirection } from '@confluence/route-manager/entry-points/Redirection.ts';
import {
	ADMIN_COMPANY_HUB_PERMISSIONS,
	SPACE_MEMBERS,
	SPACE_SETTINGS_ACCESS_LINKS,
} from '@confluence/named-routes/entry-points/routes.ts';
import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';

const SpacePermissionsSettingsRouteComponent = LoadablePaint({
	loader: async () =>
		import(
			/* webpackChunkName: "loadable-SpacePermissionSettingsRouteComponent" */ './SpacePermissionsSettingsRouteComponent.tsx'
		),
});

const DEFAULT_TAB = 'users';

export const SpacePermissionsSettingsRoute: {
	(routeMatch: RouteMatch): React.JSX.Element;
	NAVIGATION_PARAMS(
		routeMatch: RouteMatch,
		isNav4Enabled: boolean,
	): {
		Screen: {
			screenEvent: {
				name: string;
				id: string;
			};
			pageState: {
				spaceKey: string;
				routeName: string;
			};
		};
		MainLayout: {
			navView: string;
			spaceKey: string;
		};
	};
} = ({
	name: routeName,
	params: { spaceKey, tab = DEFAULT_TAB },
}: RouteMatch): React.JSX.Element => {
	//
	// Warning:
	// "Hub permissions" is a customization of "Space permissions" predating RBAC so
	// "Hub permissions" is to be used in the Company Hub-type of space and
	// "Space permissions" or RBAC are to be used in all other types of space!
	//
	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	const { isRbacEnabled } = useSpaceRoleMode();

	if (tab === 'public-links' && expValEquals('shareable_links_m0', 'isEnabled', true)) {
		return <Redirection name={SPACE_SETTINGS_ACCESS_LINKS.name} params={{ spaceKey }} />;
	}

	if (isCompanyHub) {
		if (routeName !== ADMIN_COMPANY_HUB_PERMISSIONS.name) {
			return <Redirection name={ADMIN_COMPANY_HUB_PERMISSIONS.name} params={{ tab: 'users' }} />;
		}
	} else {
		//RBAC Milestone 2 Redirect Logic in case users try to access via url
		const isSpacePermissionsTab = tab === 'users' || tab === 'groups' || tab === 'guests';
		if (isSpacePermissionsTab && isRbacEnabled) {
			return <Redirection name={SPACE_MEMBERS.name} params={{ spaceKey }} />;
		}
	}

	return <SpacePermissionsSettingsRouteComponent spaceKey={spaceKey} tab={tab} />;
};

SpacePermissionsSettingsRoute.NAVIGATION_PARAMS = (
	routeMatch: RouteMatch,
	isNav4Enabled: boolean,
): {
	Screen: {
		screenEvent: {
			name: string;
			id: string;
		};
		pageState: {
			spaceKey: string;
			routeName: string;
		};
	};
	MainLayout: {
		navView: string;
		spaceKey: string;
	};
} => {
	const {
		name,
		params: { spaceKey, tab = DEFAULT_TAB },
	} = routeMatch;

	return {
		Screen: {
			screenEvent: {
				name:
					tab === 'public-links'
						? 'spacePermissionsPublicLinksScreen'
						: `${tab}SpacePermissionsScreen`,
				id: spaceKey,
			},
			pageState: { spaceKey, routeName: name },
		},
		MainLayout: {
			navView: getSideNav(routeMatch, isNav4Enabled),
			spaceKey,
		},
	};
};

const getSideNav = (routeMatch: RouteMatch, isNav4Enabled: boolean) =>
	isCompanyHubSpaceKey(routeMatch.params.spaceKey) && getCompanyHubSideNav(routeMatch)
		? ADMIN_HOME_ID
		: isNav4Enabled
			? CONTAINER_ADMIN_HOME_ID
			: CONTAINER_HOME_ID;
